/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ResultListDataRepresentationTaskRepresentation, TaskRepresentation } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
export class TasksApi extends BaseApi {
    createIdentityLink(taskId, identityLinkRepresentation) {
        throwIfNotDefined(taskId, 'taskId');
        throwIfNotDefined(identityLinkRepresentation, 'identityLinkRepresentation');
        const pathParams = {
            taskId
        };
        return this.post({
            path: '/api/enterprise/tasks/{taskId}/identitylinks',
            pathParams,
            bodyParam: identityLinkRepresentation
        });
    }
    createNewTask(taskRepresentation) {
        throwIfNotDefined(taskRepresentation, 'taskRepresentation');
        return this.post({
            path: '/api/enterprise/tasks',
            bodyParam: taskRepresentation,
            returnType: TaskRepresentation
        });
    }
    deleteIdentityLink(taskId, family, identityId, type) {
        throwIfNotDefined(taskId, 'taskId');
        throwIfNotDefined(family, 'family');
        throwIfNotDefined(identityId, 'identityId');
        throwIfNotDefined(type, 'type');
        const pathParams = {
            taskId,
            family,
            identityId,
            type
        };
        return this.delete({
            path: '/api/enterprise/tasks/{taskId}/identitylinks/{family}/{identityId}/{type}',
            pathParams
        });
    }
    deleteTask(taskId) {
        throwIfNotDefined(taskId, 'taskId');
        const pathParams = {
            taskId
        };
        return this.delete({
            path: '/api/enterprise/tasks/{taskId}',
            pathParams
        });
    }
    filterTasks(tasksFilter) {
        throwIfNotDefined(tasksFilter, 'tasksFilter');
        return this.post({
            path: '/api/enterprise/tasks/filter',
            bodyParam: tasksFilter,
            returnType: ResultListDataRepresentationTaskRepresentation
        });
    }
    getIdentityLinkType(taskId, family, identityId, type) {
        throwIfNotDefined(taskId, 'taskId');
        throwIfNotDefined(family, 'family');
        throwIfNotDefined(identityId, 'identityId');
        throwIfNotDefined(type, 'type');
        const pathParams = {
            taskId,
            family,
            identityId,
            type
        };
        return this.get({
            path: '/api/enterprise/tasks/{taskId}/identitylinks/{family}/{identityId}/{type}',
            pathParams
        });
    }
    getIdentityLinksForFamily(taskId, family) {
        throwIfNotDefined(taskId, 'taskId');
        throwIfNotDefined(family, 'family');
        const pathParams = {
            taskId,
            family
        };
        return this.get({
            path: '/api/enterprise/tasks/{taskId}/identitylinks/{family}',
            pathParams
        });
    }
    getIdentityLinks(taskId) {
        throwIfNotDefined(taskId, 'taskId');
        const pathParams = {
            taskId
        };
        return this.get({
            path: '/api/enterprise/tasks/{taskId}/identitylinks',
            pathParams
        });
    }
    getTaskAuditLog(taskId) {
        throwIfNotDefined(taskId, 'taskId');
        const pathParams = {
            taskId
        };
        return this.get({
            path: '/api/enterprise/tasks/{taskId}/audit',
            pathParams
        });
    }
    getTaskAuditPdf(taskId) {
        throwIfNotDefined(taskId, 'taskId');
        const pathParams = {
            taskId
        };
        return this.get({
            path: 'app/rest/tasks/{taskId}/audit',
            pathParams,
            returnType: 'blob'
        });
    }
    getTask(taskId) {
        throwIfNotDefined(taskId, 'taskId');
        const pathParams = {
            taskId
        };
        return this.get({
            path: '/api/enterprise/tasks/{taskId}',
            pathParams,
            returnType: TaskRepresentation
        });
    }
    listHistoricTasks(queryRequest) {
        throwIfNotDefined(queryRequest, 'queryRequest');
        return this.post({
            path: '/api/enterprise/historic-tasks/query',
            bodyParam: queryRequest,
            returnType: ResultListDataRepresentationTaskRepresentation
        });
    }
    listTasks(tasksQuery) {
        throwIfNotDefined(tasksQuery, 'tasksQuery');
        return this.post({
            path: '/api/enterprise/tasks/query',
            bodyParam: tasksQuery,
            returnType: ResultListDataRepresentationTaskRepresentation
        });
    }
    updateTask(taskId, updated) {
        throwIfNotDefined(taskId, 'taskId');
        throwIfNotDefined(updated, 'updated');
        const pathParams = {
            taskId
        };
        return this.put({
            path: '/api/enterprise/tasks/{taskId}',
            pathParams,
            bodyParam: updated,
            returnType: TaskRepresentation
        });
    }
}
